import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Container, 
  VStack, 
  Heading, 
  Text, 
  Button,
  useToast,
  Progress
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../context/GameContext';
import BlurredImage from './BlurredImage';
import GuessInput from './GuessInput';
import GuessCounter from './GuessCounter';
import GameOverlay from './GameOverlay';
import GameStats from './GameStats';

const Game = () => {
  const { 
    image, 
    loading, 
    error, 
    blurLevel, 
    gameActive, 
    guessesRemaining,
    sessionLives,
    currentWord,
    category,
    processGuess,
    resetGame,
    score,
    setScore
  } = useGame();
  
  const [showOverlay, setShowOverlay] = useState(false);
  const [isCorrectGuess, setIsCorrectGuess] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  // Redirect to home if no game is active and not loading
  useEffect(() => {
    if (!gameActive && !loading && !image) {
      navigate('/');
    }
  }, [gameActive, loading, image, navigate]);

  // Handle errors
  useEffect(() => {
    if (error) {
      if (!toast.isActive('request-failed')) { // Prevent duplicate error toasts
        toast({
          id: 'request-failed',
          title: 'Error',
          description: error,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  }, [error, toast]);

  // Handle guess submission
  const handleGuess = (guess) => {
    if (!gameActive) return;

    const isCorrect = processGuess(guess);
    
    if (isCorrect) {
      setIsCorrectGuess(true);
      setShowOverlay(true);
      
      toast({
        title: 'Correct!',
        description: `You earned ${guessesRemaining * 100} points!`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } else {
      setIsCorrectGuess(false);
      
      if (guessesRemaining <= 1) {
        setShowOverlay(true);
        
        toast({
          title: 'Game Over',
          description: `The correct word was: ${currentWord}`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Incorrect',
          description: `Try again! ${guessesRemaining - 1} guesses left.`,
          status: 'warning',
          duration: 2000,
          isClosable: true,
        });
      }
    }
  };

  const handleOverlayClose = () => {
    setShowOverlay(false);
    resetGame();
    navigate('/');
  };

  const handleHintPenalty = (penalty) => {
    // Deduct points from the score, allowing negative values
    const newScore = score - penalty;
    setScore(newScore);
  };

  return (
    <Container maxW="container.md" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box textAlign="center" mb={2}>
          <Heading 
            as="h1" 
            size="xl" 
            bgGradient="linear(to-r, brand.500, purple.500)" 
            bgClip="text"
          >
            VisualEyes
          </Heading>
          <Text fontSize="lg" color="gray.600">
            What do you see?
          </Text>
        </Box>
        
        {/* Loading Progress */}
        {loading && (
          <Box textAlign="center" py={4}>
            <Progress 
              size="xs" 
              isIndeterminate 
              colorScheme="brand"
              borderRadius="full"
              mb={2}
            />
            <Text color="gray.600" fontSize="sm">
              {currentWord ? 'Generating your image, please wait...' : 'Loading image...'}
            </Text>
          </Box>
        )}
        
        {/* Main Game Area */}
        <Box position="relative">
          {/* Image with Blur Effect */}
          <BlurredImage 
            imageUrl={image} 
            blurLevel={blurLevel} 
            loading={loading} 
          />
          
          {/* Game Overlay (for correct/incorrect notifications) */}
          <GameOverlay 
            show={showOverlay}
            isCorrect={isCorrectGuess}
            correctWord={currentWord}
            guessesLeft={guessesRemaining}
            onClose={handleOverlayClose}
          />
        </Box>
        
        {/* Guesses Counter */}
        <GuessCounter 
          guessesRemaining={guessesRemaining} 
        />
        
        {/* Guess Input */}
        <GuessInput 
          onSubmitGuess={handleGuess} 
          gameActive={gameActive && !showOverlay}
          category={category}
          currentWord={currentWord}
          onHintPenalty={handleHintPenalty}
        />
        
        {/* Back Button */}
        <Box textAlign="center" pt={4}>
          <Button
            variant="outline"
            colorScheme="gray"
            onClick={() => {
              resetGame();
              navigate('/');
            }}
          >
            Give Up & Start New Game
          </Button>
        </Box>

        <GameStats 
          score={score} 
          sessionLives={sessionLives}
        />
      </VStack>
    </Container>
  );
};

export default Game;